import type { CurrentUser } from '@zhiyue/core';
import { BizAppConfig } from 'config/application.manifest';
import React from 'react';
import { fetchOpenPlatformToken } from '@/services/openPlatformToken';

export type MemberDirectoryItem = Record<string, unknown> & {
  memberId?: string;
  name?: string;
};

type MemberDirectoryCurrentUser = Pick<
  CurrentUser,
  'id' | 'memberUuid' | 'tenantUuid'
> & {
  organizationUuid?: string;
};

type MemberDirectoryState = {
  loadedKey: string;
  memberList: MemberDirectoryItem[];
};

type MemberDirectoryResponse = {
  list?: MemberDirectoryItem[];
  total?: number;
  pageNo?: number;
  pageSize?: number;
};

const PAGE_SIZE = 100;
const listeners = new Set<() => void>();

let state: MemberDirectoryState = {
  loadedKey: '',
  memberList: [],
};
let loadingKey = '';
let loadingPromise: Promise<void> | null = null;

const normalizeText = (value: unknown) => String(value ?? '').trim();

const emitChange = () => {
  listeners.forEach((listener) => {
    listener();
  });
};

const setState = (nextState: MemberDirectoryState) => {
  state = nextState;
  emitChange();
};

const getLoadKey = (currentUser?: Partial<MemberDirectoryCurrentUser> | null) =>
  [
    normalizeText(currentUser?.tenantUuid),
    normalizeText(currentUser?.organizationUuid),
    normalizeText(currentUser?.memberUuid),
    normalizeText(currentUser?.id),
  ]
    .filter(Boolean)
    .join('|');

const getMemberDirectoryBaseUrl = () => {
  const openServiceUrl = normalizeText(BizAppConfig.appOpenServiceUrl).replace(
    /\/+$/,
    '',
  );
  const gatewayPath = normalizeText(BizAppConfig.appOpenApiGatewayPath).replace(
    /^\/+|\/+$/g,
    '',
  );
  const categoryCode = normalizeText(
    BizAppConfig.appOrganizationAdminCategoryCode,
  ).replace(/^\/+|\/+$/g, '');
  if (!openServiceUrl || !gatewayPath || !categoryCode) {
    throw new Error('开放平台成员目录配置不完整');
  }
  return `${openServiceUrl}/${gatewayPath}/${categoryCode}/api/v1/members`;
};

const createMemberDirectoryRequest = (
  memberUuid: string,
  accessToken: string,
) => {
  return async (pageNo: number) => {
    const headers = new Headers({
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
      memberUuid,
    });

    const response = await fetch(
      `${getMemberDirectoryBaseUrl()}?pageNo=${pageNo}&pageSize=${PAGE_SIZE}`,
      {
        method: 'GET',
        headers,
      },
    );

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }

    return response.json() as Promise<MemberDirectoryResponse>;
  };
};

export const clearMemberDirectoryCache = () => {
  loadingKey = '';
  loadingPromise = null;
  setState({
    loadedKey: '',
    memberList: [],
  });
};

export const preloadMemberDirectory = async (
  currentUser?: Partial<MemberDirectoryCurrentUser> | null,
) => {
  const tenantUuid = normalizeText(currentUser?.tenantUuid);
  const organizationUuid = normalizeText(currentUser?.organizationUuid);
  const loadKey = getLoadKey(currentUser);

  if (!tenantUuid || !organizationUuid || !loadKey) {
    clearMemberDirectoryCache();
    return;
  }

  if (state.loadedKey === loadKey && state.memberList.length > 0) {
    return;
  }

  if (loadingKey === loadKey && loadingPromise) {
    return loadingPromise;
  }

  if (state.loadedKey !== loadKey) {
    setState({
      loadedKey: '',
      memberList: [],
    });
  }

  loadingKey = loadKey;
  loadingPromise = (async () => {
    let pageNo = 1;
    let memberList: MemberDirectoryItem[] = [];
    const { accessToken } = await fetchOpenPlatformToken({
      tenantUuid,
      organizationUuid,
      memberUuid: normalizeText(currentUser?.memberUuid),
    });
    const requestMemberPage = createMemberDirectoryRequest(
      normalizeText(currentUser?.memberUuid),
      accessToken,
    );

    while (true) {
      const result = await requestMemberPage(pageNo);
      const list = result?.list || [];

      memberList = memberList.concat(list);

      if (!list.length) {
        break;
      }

      const loadedCount = pageNo * PAGE_SIZE;
      const total = Number(result?.total || 0);
      if (loadedCount >= total || list.length < PAGE_SIZE) {
        break;
      }

      pageNo += 1;
    }

    setState({
      loadedKey: loadKey,
      memberList,
    });
  })()
    .catch(() => {
      setState({
        loadedKey: loadKey,
        memberList: [],
      });
    })
    .finally(() => {
      if (loadingKey === loadKey) {
        loadingKey = '';
        loadingPromise = null;
      }
    });

  return loadingPromise;
};

export const subscribeMemberDirectory = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getMemberDirectorySnapshot = () => state.memberList;

export const useMemberDirectory = () =>
  React.useSyncExternalStore(
    subscribeMemberDirectory,
    getMemberDirectorySnapshot,
    getMemberDirectorySnapshot,
  );
